<template>
  <TourFilterPopupWrapper
    v-if="groups.length"
    :title="title"
    :items-count="itemsCount"
    is-modal-only
    :static-title="t('tour-activities.popup-title')"
    modal-class="tours-activities-modal"
    filter-key="toursActivities"
    class="filter-tours-activities"
    :is-active="isActive"
    is-full-height
    :modal-size="UiModalSize.M"
    :is-disabled-reset-button="isDisabledResetButton"
    is-footer-buttons-vertical
    :is-scroll="isScroll"
    v-bind="$attrs"
    @close="onClearSearchInput"
    @apply="onClearSearchInput"
  >
    <div class="popup__content">
      <div class="tour-activities-search-block">
        <SearchIcon class="tour-activities-search-icon" />
        <UiInput
          :value="searchInput"
          is-full-width
          :type="UiInputTypes.TEXT"
          :placeholder="t('tour-activities.inputPlaceholder')"
          :class="searchInputClass"
          @input-value="onSearchInput"
        />
        <ClearSearchIcon
          v-if="searchInput"
          class="tour-activities-search__close-icon"
          @click.stop="() => (searchInput = '')"
        />
      </div>

      <ToursActivitiesPopupContent
        :groups="groups"
        :input-value="searchInput"
      />
    </div>
  </TourFilterPopupWrapper>
</template>

<script setup lang="ts">
  import { computed, ref } from 'vue';
  import { TourFilterPopupWrapper } from '@/entities/filter/popup-wrapper';
  import { UiInput, UiInputTypes } from '@experience/core-input-ui';
  import { ToursActivitiesPopupContent } from '@/entities/tour-activities';
  import { useToursActivitiesAsChipsGroups, useTourActivitiesTitle } from '@/features/filter/tour-activities';
  import SearchIcon from '@/shared/ui/icons/SearchIcon.vue';
  import { ClearSearchIcon } from '@/shared/ui/icons/clearSearchIcon';
  import ru from '../i18n/ru.json';
  import { useI18n } from '@experience/i18n';
  import { UiModalSize } from '@experience/core-modal-ui';
  import { locales } from '../i18n';

  defineOptions({ inheritAttrs: false });

  type MessageSchema = typeof ru;

  const { t } = useI18n<
    {
      message: MessageSchema;
    },
    'ru'
  >({
    inheritLocale: true,
    messages: {
      ru,
    },
  });

  interface Props {
    isScroll?: boolean;
  }

  defineProps<Props>();

  const { title, itemsCount, isActive, isDisabledResetButton } = useTourActivitiesTitle(locales.ru);

  const groups = useToursActivitiesAsChipsGroups();

  const searchInput = ref('');
  const searchInputClass = computed(() => ['tour-activities-search', { input_icon_right: searchInput.value }]);

  const onSearchInput = (v: string | number) => (searchInput.value = `${v}`);
  const onClearSearchInput = () => (searchInput.value = '');
</script>

<style scoped lang="scss">
  @use '@experience/shared/dist/assets/adaptivity' as adaptivity;

  .ui-modal__body-content-wrapper {
    padding-top: 0 !important;
  }

  .tour-activities-search-block {
    position: relative;
    z-index: 1;
    margin-bottom: 24px;
  }

  .tour-activities-search {
    width: 100%;
    background: var(--comp-input-color-background);
  }

  .tour-activities-search:deep(.input__inner-wrapper) {
    padding-left: 44px;
  }

  .tour-activities-search.input_icon_right:deep(.input__inner-wrapper) {
    padding-right: 44px;
  }

  .tour-activities-search:deep(input) {
    font-size: var(--global-typography-size-400) !important;
    text-overflow: ellipsis;
  }

  .tour-activities-search-icon {
    position: absolute;
    top: 14px;
    bottom: 0;
    z-index: 3;
    display: flex;
    width: 48px;
    pointer-events: none;
  }

  .tour-activities-search__close-icon {
    position: absolute;
    top: 14px;
    right: 12px;
    z-index: 99;
    cursor: pointer;
  }
</style>
<style lang="scss">
  .tours-activities-modal {
    .ui-modal__body-content-wrapper {
      padding-top: 8px;
      padding-bottom: 0;
    }

    .ui-modal-header {
      padding-bottom: 16px;
    }
  }
</style>
